<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/24/outline'

/**
 * Branded, plně přístupný výběr služby (ARIA listbox s aria-activedescendant).
 * Ovládání myší i klávesnicí (šipky, Home/End, Enter/Space, Esc), zavření
 * kliknutím mimo. v-model nese id vybrané položky ('' = nevybráno).
 */
const props = defineProps({
    modelValue: { type: String, default: '' },
    groups: { type: Array, required: true },
    placeholder: { type: String, default: 'Vyberte…' },
    noneLabel: { type: String, default: 'Obecná poptávka (nevybráno)' },
    id: { type: String, default: 'service-select' },
    ariaLabel: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])

const open = ref(false)
const activeIndex = ref(-1)
const root = ref(null)
const listRef = ref(null)
const triggerRef = ref(null)

// Plochý seznam vybíratelných řádků v pořadí zobrazení (nejdřív "nevybráno").
const flatOptions = computed(() => {
    const rows = [{ id: '', name: props.noneLabel, price: '', isNone: true }]

    for (const group of props.groups) {
        for (const option of group.options) {
            rows.push({ id: option.id, name: option.name, price: option.price ?? '', isNone: false })
        }
    }

    return rows
})

const selected = computed(() => flatOptions.value.find((o) => !o.isNone && o.id === props.modelValue) ?? null)

const optionDomId = (id) => `${props.id}-opt-${id || 'none'}`
const flatIndexOf = (id) => flatOptions.value.findIndex((o) => o.id === id)

const activeDescendant = computed(() => {
    if (!open.value || activeIndex.value < 0) {
        return undefined
    }

    return optionDomId(flatOptions.value[activeIndex.value]?.id ?? '')
})

const scrollActiveIntoView = () => {
    const row = flatOptions.value[activeIndex.value]

    if (!row || !listRef.value) {
        return
    }

    listRef.value.querySelector(`#${optionDomId(row.id)}`)?.scrollIntoView({ block: 'nearest' })
}

const openMenu = async () => {
    open.value = true
    const current = flatIndexOf(props.modelValue)
    activeIndex.value = current >= 0 ? current : 0
    await nextTick()
    scrollActiveIntoView()
}

const closeMenu = () => {
    open.value = false
    activeIndex.value = -1
}

const toggle = () => (open.value ? closeMenu() : openMenu())

const select = (id) => {
    emit('update:modelValue', id)
    closeMenu()
    triggerRef.value?.focus()
}

const move = (delta) => {
    const count = flatOptions.value.length
    activeIndex.value = (activeIndex.value + delta + count) % count
    scrollActiveIntoView()
}

const onKeydown = (event) => {
    if (!open.value) {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
            event.preventDefault()
            openMenu()
        }

        return
    }

    switch (event.key) {
        case 'ArrowDown':
            event.preventDefault()
            move(1)
            break
        case 'ArrowUp':
            event.preventDefault()
            move(-1)
            break
        case 'Home':
            event.preventDefault()
            activeIndex.value = 0
            scrollActiveIntoView()
            break
        case 'End':
            event.preventDefault()
            activeIndex.value = flatOptions.value.length - 1
            scrollActiveIntoView()
            break
        case 'Enter':
        case ' ':
            event.preventDefault()
            select(flatOptions.value[activeIndex.value]?.id ?? '')
            break
        case 'Escape':
            event.preventDefault()
            closeMenu()
            break
        case 'Tab':
            closeMenu()
            break
    }
}

const onPointerDown = (event) => {
    if (open.value && root.value && !root.value.contains(event.target)) {
        closeMenu()
    }
}

onMounted(() => document.addEventListener('mousedown', onPointerDown))
onBeforeUnmount(() => document.removeEventListener('mousedown', onPointerDown))

const triggerClass =
    'mt-2 flex w-full items-center justify-between rounded-md border border-zinc-300 bg-white px-3.5 py-3 text-left text-base shadow-sm transition-colors focus:border-brand-900 focus:ring-2 focus:ring-brand-900/10 focus:outline-none'
const optionClass = 'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5'
</script>

<template>
    <div
        class="relative"
        ref="root"
    >
        <button
            class="text-brand-900"
            ref="triggerRef"
            :id="id"
            :class="triggerClass"
            type="button"
            role="combobox"
            aria-haspopup="listbox"
            :aria-expanded="open"
            :aria-controls="`${id}-listbox`"
            :aria-activedescendant="activeDescendant"
            :aria-label="ariaLabel || undefined"
            @click="toggle"
            @keydown="onKeydown"
        >
            <span class="flex min-w-0 items-center gap-2">
                <span
                    class="bg-accent-500 size-2 shrink-0 rounded-full"
                    v-if="selected"
                    aria-hidden="true"
                />

                <span
                    class="truncate"
                    :class="selected ? 'text-brand-900 font-medium' : 'text-zinc-500'"
                >
                    {{ selected ? selected.name : placeholder }}
                </span>

                <span
                    class="shrink-0 text-sm text-zinc-500"
                    v-if="selected?.price"
                >
                    {{ selected.price }}
                </span>
            </span>

            <ChevronUpDownIcon
                class="ml-2 size-5 shrink-0 text-zinc-400"
                aria-hidden="true"
            />
        </button>

        <ul
            class="absolute right-0 left-0 z-20 mt-2 max-h-80 overflow-auto rounded-xl border border-zinc-200 bg-white p-1.5 shadow-lg shadow-zinc-900/5"
            v-if="open"
            :id="`${id}-listbox`"
            ref="listRef"
            role="listbox"
            :aria-label="ariaLabel || undefined"
        >
            <li
                :id="optionDomId('')"
                :class="[optionClass, activeIndex === 0 ? 'bg-brand-50' : '']"
                role="option"
                :aria-selected="!modelValue"
                @click="select('')"
                @mousemove="activeIndex = 0"
            >
                <span class="text-zinc-500">{{ noneLabel }}</span>

                <CheckIcon
                    class="text-brand-900 ml-auto size-4 shrink-0"
                    v-if="!modelValue"
                    aria-hidden="true"
                />
            </li>

            <template
                v-for="group in groups"
                :key="group.label"
            >
                <li
                    class="px-3 pt-3 pb-1 text-xs font-semibold tracking-wider text-zinc-400 uppercase"
                    role="presentation"
                >
                    {{ group.label }}
                </li>

                <li
                    v-for="option in group.options"
                    :id="optionDomId(option.id)"
                    :key="option.id"
                    :class="[optionClass, flatIndexOf(option.id) === activeIndex ? 'bg-brand-50' : '']"
                    role="option"
                    :aria-selected="option.id === modelValue"
                    @click="select(option.id)"
                    @mousemove="activeIndex = flatIndexOf(option.id)"
                >
                    <span
                        class="truncate"
                        :class="option.id === modelValue ? 'text-brand-900 font-semibold' : 'text-brand-900'"
                    >
                        {{ option.name }}
                    </span>

                    <span class="ml-auto flex shrink-0 items-center gap-2">
                        <span
                            class="text-sm text-zinc-500"
                            v-if="option.price"
                        >
                            {{ option.price }}
                        </span>

                        <CheckIcon
                            class="text-brand-900 size-4"
                            v-if="option.id === modelValue"
                            aria-hidden="true"
                        />
                    </span>
                </li>
            </template>
        </ul>
    </div>
</template>
